import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Brain,
  Filter,
  RefreshCw,
  Sparkles,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { CompraAgilOpportunityCard } from "@/components/compra-agil/CompraAgilOpportunityCard";
import { KpiTile } from "@/components/intelligence/KpiTile";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { compraAgilIaService } from "@/services/compra-agil-ia.service";
import { formatCLP } from "@/lib/mock-data";
import { cn } from "@/lib/utils";
import type { Recommendation, Riesgo } from "@/types";

export const Route = createFileRoute("/compra-agil-ia/")({
  head: () => ({
    meta: [
      { title: "Compra Ágil IA — LicitaPro" },
      {
        name: "description",
        content:
          "Motor de recomendación IA sobre Compra Ágil: matching, scoring y probabilidad de adjudicación.",
      },
    ],
  }),
  component: CompraAgilIaPage,
});

const recOptions: { value: Recommendation; label: string }[] = [
  { value: "postular", label: "Postular" },
  { value: "revisar", label: "Revisar" },
  { value: "no_postular", label: "No postular" },
];

const riesgoOptions: Riesgo[] = ["bajo", "medio", "alto"];

const PAGE_SIZE = 50;

function CompraAgilIaPage() {
  // null = usar el umbral configurado en el perfil del tenant.
  const [minScore, setMinScore] = useState<number | null>(null);
  const [minScoreInput, setMinScoreInput] = useState<number | null>(null);
  const [rec, setRec] = useState<Recommendation[]>([]);
  const [riesgos, setRiesgos] = useState<Riesgo[]>([]);
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["compra-agil-ia", "list", page, minScore],
    queryFn: () =>
      compraAgilIaService.list(minScore != null ? { minScore } : undefined, page, PAGE_SIZE),
    placeholderData: (prev) => prev,
    // Mientras el embudo corre por primera vez, refrescamos para ver el avance.
    refetchInterval: (q) => (q.state.data?.estadoProceso.procesando ? 4000 : false),
  });

  const recalcular = useMutation({
    mutationFn: () => compraAgilIaService.procesar("full"),
    onSuccess: (r) => {
      toast.success(
        r.yaEnCurso
          ? "Ya hay un recálculo en curso"
          : "Recalculando todas las Compras Ágiles con tu perfil",
      );
      void qc.invalidateQueries({ queryKey: ["compra-agil-ia"] });
    },
    onError: () => toast.error("No se pudo iniciar el recálculo"),
  });

  const enriquecer = useMutation({
    mutationFn: () => compraAgilIaService.evaluar(),
    onSuccess: (r) => {
      if (r.evaluadas === 0 && r.errores > 0) {
        toast.error(r.error ?? "El motor IA no pudo evaluar ninguna Compra Ágil");
      } else {
        toast.success(
          `${r.evaluadas} enriquecidas con IA${r.errores ? ` · ${r.errores} con error` : ""}`,
        );
      }
      void qc.invalidateQueries({ queryKey: ["compra-agil-ia"] });
    },
    onError: () => toast.error("No se pudo ejecutar el motor IA"),
  });

  const oportunidades = useMemo(() => query.data?.items ?? [], [query.data]);
  const embudo = query.data?.embudo;
  const proceso = query.data?.estadoProceso;
  const totalSobreUmbral = query.data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalSobreUmbral / PAGE_SIZE));
  const umbralActual = minScoreInput ?? minScore ?? embudo?.umbral ?? 0;

  const filtered = useMemo(
    () =>
      oportunidades.filter((o) => {
        if (rec.length && !rec.includes(o.scoring.recommendation)) return false;
        if (riesgos.length && !riesgos.includes(o.scoring.riesgo)) return false;
        return true;
      }),
    [oportunidades, rec, riesgos],
  );

  const recomendadas = oportunidades.filter((o) => o.scoring.recommendation === "postular");
  const monto = recomendadas.reduce((a, o) => a + (o.compraAgil.montoDisponibleCLP ?? 0), 0);
  const avg = Math.round(
    oportunidades.reduce((a, o) => a + o.scoring.score, 0) / Math.max(oportunidades.length, 1),
  );
  const riesgosAltos = oportunidades.filter((o) => o.scoring.riesgo === "alto").length;

  function toggle<T>(arr: T[], v: T, setter: (n: T[]) => void) {
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  }

  /** Aplica el umbral en el servidor (el listado se filtra en la BD, no en pantalla). */
  function aplicarUmbral() {
    if (minScoreInput == null) return;
    setPage(1);
    setMinScore(minScoreInput);
  }

  const trabajando = recalcular.isPending || enriquecer.isPending || proceso?.procesando;

  return (
    <AppShell
      title="Compra Ágil IA"
      subtitle="Motor de recomendación sobre Compra Ágil · Analizadas con tu perfil empresarial"
      actions={
        <div className="hidden md:flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-muted-foreground">
          <Zap className="size-3.5 text-primary" />
          IA activa
        </div>
      }
    >
      {/* IA banner */}
      <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 mb-6">
        <div className="absolute inset-0 grid-pattern opacity-[0.03]" />
        <div className="relative flex items-start gap-3">
          <div className="size-10 rounded-lg bg-gradient-primary grid place-items-center shadow-glow shrink-0">
            <Sparkles className="size-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground">
              El copiloto recomienda{" "}
              <span className="text-primary">{recomendadas.length} Compras Ágiles</span> con alto
              match
            </div>
            <div className="text-xs text-muted-foreground">
              Score promedio {avg} · Potencial combinado {formatCLP(monto)}
            </div>
          </div>
        </div>
      </div>

      {/* Embudo del motor */}
      {embudo && (
        <div className="rounded-xl border border-border bg-card p-4 mb-6">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="text-foreground font-medium tabular-nums">
              {embudo.vigentes.toLocaleString("es-CL")}
            </span>
            vigentes
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.prefiltradas.toLocaleString("es-CL")}
            </span>
            compatibles con tu perfil
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.sobreUmbral.toLocaleString("es-CL")}
            </span>
            sobre umbral ({embudo.umbral}){" "}
            <span className="text-muted-foreground/50">→</span>
            <span className="text-foreground font-medium tabular-nums">
              {embudo.evaluadasIa.toLocaleString("es-CL")}
            </span>
            con análisis IA
          </div>
          {proceso?.procesando && (
            <div className="mt-2 text-xs text-primary tabular-nums">
              Procesando el universo… {proceso.listas.toLocaleString("es-CL")} /{" "}
              {proceso.total.toLocaleString("es-CL")}
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiTile
          icon={Target}
          label="Sobre umbral"
          value={totalSobreUmbral}
          accent="primary"
          hint="Compatibles con tu perfil"
        />
        <KpiTile
          icon={Trophy}
          label="Recomendadas"
          value={recomendadas.length}
          accent="success"
          hint="Recomendación: POSTULAR"
        />
        <KpiTile
          icon={Brain}
          label="Score promedio"
          value={avg}
          accent="info"
          hint="Compatibilidad global"
        />
        <KpiTile
          icon={AlertTriangle}
          label="Riesgos altos"
          value={riesgosAltos}
          accent="destructive"
          hint="Requieren revisión humana"
        />
      </div>

      {/* Filtros */}
      <div className="rounded-xl border border-border bg-card p-4 mb-6">
        <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
          <Filter className="size-3.5" />
          Filtros del motor
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Min score</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={umbralActual}
              onChange={(e) => setMinScoreInput(Number(e.target.value))}
              onPointerUp={aplicarUmbral}
              onKeyUp={aplicarUmbral}
              className="w-32 accent-primary"
              aria-label="Score mínimo"
            />
            <span className="text-xs font-mono tabular-nums text-foreground w-8">
              {umbralActual}
            </span>
            {minScore != null && (
              <button
                onClick={() => {
                  setMinScoreInput(null);
                  setMinScore(null);
                  setPage(1);
                }}
                className="text-[11px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:bg-muted"
              >
                Usar umbral del perfil
              </button>
            )}
          </div>
          <button
            onClick={() => recalcular.mutate()}
            disabled={trabajando}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
          >
            <RefreshCw className={cn("size-3.5", recalcular.isPending && "animate-spin")} />
            Recalcular todo
          </button>
          <button
            onClick={() => enriquecer.mutate()}
            disabled={trabajando}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 disabled:opacity-40"
            title="Evalúa con el motor IA las mejores Compras Ágiles que aún no tienen análisis"
          >
            <Sparkles className={cn("size-3.5", enriquecer.isPending && "animate-spin")} />
            Enriquecer con IA
          </button>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Recomendación:</span>
            {recOptions.map((r) => (
              <button
                key={r.value}
                onClick={() => toggle(rec, r.value, setRec)}
                className={cn(
                  "text-[11px] px-2 py-1 rounded-full border transition",
                  rec.includes(r.value)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Riesgo:</span>
            {riesgoOptions.map((r) => (
              <button
                key={r}
                onClick={() => toggle(riesgos, r, setRiesgos)}
                className={cn(
                  "text-[11px] px-2 py-1 rounded-full border capitalize transition",
                  riesgos.includes(r)
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-muted-foreground hover:bg-muted",
                )}
              >
                {r}
              </button>
            ))}
          </div>
          <div className="ml-auto text-xs text-muted-foreground tabular-nums">
            Mostrando <span className="text-foreground font-medium">{filtered.length}</span> de{" "}
            {totalSobreUmbral.toLocaleString("es-CL")} · página {page} de {totalPaginas}
          </div>
        </div>
      </div>

      {/* Grid */}
      {query.isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-40 rounded-xl border border-border bg-card animate-pulse" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="No se pudieron cargar las Compras Ágiles"
          description="Revisa tu sesión o vuelve a intentarlo en unos segundos."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sparkles}
          title={
            proceso?.procesando
              ? "Procesando tus Compras Ágiles"
              : totalSobreUmbral === 0
                ? "Aún no hay Compras Ágiles sobre tu umbral"
                : "Sin resultados con los filtros actuales"
          }
          description={
            proceso?.procesando
              ? "Estamos cruzando el universo vigente de Compra Ágil con el perfil de tu empresa. Esto puede tardar unos minutos la primera vez."
              : totalSobreUmbral === 0
                ? "Baja el score mínimo, completa el perfil de tu empresa o pulsa «Recalcular todo». Ten en cuenta que Compra Ágil tiene un universo vigente mucho más chico que Licitaciones."
                : "Ajusta el score mínimo o quita filtros para ver más resultados."
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
            {filtered.map((o) => (
              <CompraAgilOpportunityCard key={o.compraAgil.id} item={o} />
            ))}
          </div>
          {totalPaginas > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-xs text-muted-foreground tabular-nums">
                {page} / {totalPaginas}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
                disabled={page >= totalPaginas || query.isFetching}
                className="text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </AppShell>
  );
}
